import { AJV_EXTENSION_REF_SCHEMA } from '../globals';
import {
    ISchemaAllOf,
    ISchemaAnyOf,
    ISchemaArray,
    ISchemaCoreType,
    ISchemaObject,
    ISchemaOneOf,
    ISchemaRefFormatted,
    ISchemaTuple,
    ISchemaType,
} from './properties';
import { SchemaStringFormat, SchemaTypeName } from './properties/primitives';

/**  Types  */
export type IGraphQLObjectMode = 'type' | 'input';

interface IGraphQLObjectProperty {
    property_name: string;
    value: string;
    is_required: boolean;
    is_array: boolean;
    is_array_content_nullable: boolean;
    is_nullable: boolean;
}

interface IGraphQLObjectType {
    name: string;
    properties: Array<IGraphQLObjectProperty>;
}

interface IGraphQLUnionType {
    name: string;
    values: Array<string>;
}

interface IGraphQLEnumType {
    name: string;
    values: Readonly<Array<string>>;
}

type IGraphQLScalar = DEFAULT_ADDITIONAL_PRIMITIVES;

interface IGraphQLOutputTypes {
    output_object_types: Array<IGraphQLObjectType>;
    output_union_types: Array<IGraphQLUnionType>;
    output_enum_types: Array<IGraphQLEnumType>;
    output_scalar_types: Array<IGraphQLScalar>;
}

interface IDeterminePropertyValueInput {
    mode: IGraphQLObjectMode;
    parent_object_name: string;
    property_name: string;
    property_schema: ISchemaType;
    is_required: boolean;
}

interface IPropertyValue extends IGraphQLOutputTypes {
    value: string;
    is_array: boolean;
    is_array_content_nullable: boolean;
    is_nullable: boolean;
    is_null: boolean;
}

/**  Globals  */
const PRIMITIVES: Record<
    Exclude<SchemaTypeName, SchemaTypeName.array | SchemaTypeName.object | SchemaTypeName.null>,
    string
> = {
    [SchemaTypeName.number]: 'Float',
    [SchemaTypeName.integer]: 'Int',
    [SchemaTypeName.string]: 'String',
    [SchemaTypeName.boolean]: 'Boolean',
};

enum DEFAULT_ADDITIONAL_PRIMITIVES {
    'Date' = 'Date',
}

/**
 * RULES
 * - If nullable, not required
 */
export function toGraphQL<InputSchema extends Readonly<ISchemaObject<any, string>>>(
    {
        name,
        schema,
        mode,
    }: {
        name: string;
        schema: InputSchema;
        mode: IGraphQLObjectMode;
    },
    {
        output_object_types,
        output_union_types,
        output_enum_types,
        output_scalar_types,
    }: IGraphQLOutputTypes = {
        output_object_types: [],
        output_union_types: [],
        output_enum_types: [],
        output_scalar_types: [],
    }
): string {
    /**
     * Set default scalars
     */
    Object.values(DEFAULT_ADDITIONAL_PRIMITIVES).forEach(scalar => {
        if (!output_scalar_types.includes(scalar)) {
            output_scalar_types.push(scalar);
        }
    });

    /**
     * This object
     */
    const properties: Array<IGraphQLObjectProperty> = [];

    for (const property_name in schema.properties) {
        const is_required = schema.required.includes(property_name);
        const property_schema = schema.properties[property_name];

        //  Translate
        const { value, is_array, is_nullable, is_array_content_nullable } = determinePropertyValue(
            {
                mode,
                parent_object_name: name,
                property_name: property_name,
                property_schema,
                is_required,
            },
            {
                output_object_types,
                output_union_types,
                output_enum_types,
                output_scalar_types,
            }
        );

        properties.push({
            property_name: property_name,
            value,
            is_required,
            is_array,
            is_nullable,
            is_array_content_nullable,
        });
    }

    //  Push this type to output
    output_object_types.unshift({
        name,
        properties,
    });

    /**
     * Stringify types, unions, enums
     */
    const stringified_object_types = output_object_types
        .map(stringifyObjectType(mode))
        .join('\n\n');
    const stringified_union_types = output_union_types.map(stringifyUnionType).join('\n\n');
    const stringified_enum_types = output_enum_types.map(stringifyEnumType).join('\n\n');
    const stringified_scalar_types = output_scalar_types.map(stringifyScalar).join('\n\n');

    // TODO: Default scalars beyond Date? e.g. EmailAddress, etc. – no: let ajv handle?
    // TODO: Handle nullability: cannot be nullable and required!

    return [
        stringified_object_types,
        stringified_union_types,
        stringified_enum_types,
        stringified_scalar_types,
    ]
        .filter(x => x)
        .join('\n\n')
        .trim();
}

/**
 * Return information on value type
 * - Build new GraphQL type (type, union, enum) as necessary
 */
function determinePropertyValue(
    {
        mode,
        parent_object_name,
        property_name,
        property_schema,
        is_required,
    }: IDeterminePropertyValueInput,
    output_types: IGraphQLOutputTypes
): IPropertyValue {
    const {
        output_object_types,
        output_union_types,
        output_enum_types,
        output_scalar_types,
    } = output_types;

    /**
     * Validate
     */
    validatePropertySchema({ property_schema });

    /**
     * Determine
     */
    const property_schema_ref = property_schema as ISchemaRefFormatted;
    const property_schema_any_of = property_schema as ISchemaAnyOf<ISchemaCoreType>;
    const property_schema_one_of = property_schema as ISchemaOneOf<ISchemaCoreType>;
    const property_schema_singular = property_schema as ISchemaCoreType;
    const property_schema_array = property_schema as ISchemaArray;
    const property_schema_tuple = property_schema as ISchemaTuple;
    const property_type_name = `${parent_object_name}__${property_name}`;

    //  -: $ref
    if (property_schema_ref[AJV_EXTENSION_REF_SCHEMA]) {
        return determinePropertyValue(
            {
                mode,
                parent_object_name,
                property_name,
                property_schema: property_schema_ref[AJV_EXTENSION_REF_SCHEMA],
                is_required,
            },
            output_types
        );
    }

    //  A: Union type
    if (property_schema_any_of.anyOf) {
        return createUnionType(
            {
                items: property_schema_any_of.anyOf,
                property_type_name,
                mode,
                parent_object_name,
                property_name,
                is_required,
            },
            output_types
        );
    } else if (property_schema_one_of.oneOf) {
        return createUnionType(
            {
                items: property_schema_one_of.oneOf,
                property_type_name,
                mode,
                parent_object_name,
                property_name,
                is_required,
            },
            output_types
        );
    }

    //  B: Nested object
    else if (property_schema_singular.type === SchemaTypeName.object) {
        //  Calling toGraphQL() will push new type to output_object_types
        //  Use `any` type to overcome Typescript's concern with recursion; it's not an issue
        toGraphQL<any>(
            {
                name: property_type_name,
                schema: property_schema_singular,
                mode,
            },
            {
                output_object_types,
                output_union_types,
                output_enum_types,
                output_scalar_types,
            }
        );

        return {
            value: property_type_name,
            is_array: false,
            is_array_content_nullable: false,

            is_null: false,
            is_nullable: false,
            output_object_types,
            output_union_types,
            output_enum_types,
            output_scalar_types,
        };
    }

    //  C1: Nested tuple
    else if (
        property_schema_singular.type === SchemaTypeName.array &&
        property_schema_tuple.prefixItems
    ) {
        const tuple_items = property_schema_tuple.prefixItems;

        /**
         * No GraphQL tuple support: Make a union type instead
         */

        //  1: Extract items
        const items: Array<ISchemaCoreType> = [];
        tuple_items.forEach(item => {
            //  Validate
            validatePropertySchema({ property_schema: item });

            //  Extract
            const any_of_many = item as ISchemaAnyOf<ISchemaCoreType>;
            const one_of_many = item as ISchemaOneOf<ISchemaCoreType>;

            if (any_of_many.anyOf) {
                items.push(...any_of_many.anyOf);
            } else if (one_of_many.oneOf) {
                items.push(...one_of_many.oneOf);
            } else {
                items.push(item as ISchemaCoreType);
            }
        });

        //  2: Create union type
        const union_item_value = createUnionType(
            {
                items,
                property_type_name,
                mode,
                parent_object_name,
                property_name,
                is_required,
            },
            output_types
        );

        return {
            ...union_item_value,
            is_array: true,
            is_array_content_nullable: union_item_value.is_nullable,
        };
    }

    // //  C2: Nested array
    else if (property_schema_singular.type === SchemaTypeName.array) {
        const item_schema = property_schema_array.items;

        const item_value = determinePropertyValue(
            {
                mode,
                parent_object_name,
                property_name,
                property_schema: item_schema,
                is_required: false,
            },
            {
                output_object_types,
                output_union_types,
                output_enum_types,
                output_scalar_types,
            }
        );

        return {
            ...item_value,
            is_array: true,
            is_array_content_nullable: item_value.is_nullable,
        };
    }

    //  D: null? Cast as string
    else if (property_schema_singular.type === SchemaTypeName.null) {
        if (is_required) {
            throw new Error(`Invalid schema: Cannot require nullable property "${property_name}"`);
        }

        return {
            value: PRIMITIVES.string,
            is_array: false,
            is_array_content_nullable: false,

            is_null: true,
            is_nullable: false,
            output_object_types,
            output_union_types,
            output_enum_types,
            output_scalar_types,
        };
    }

    //  E: Enum
    else if (
        property_schema_singular.type === SchemaTypeName.string &&
        property_schema_singular.format !== SchemaStringFormat.date_time &&
        (property_schema_singular.const || property_schema_singular.enum?.length)
    ) {
        //  Push new enum
        const values = property_schema_singular.const
            ? [property_schema_singular.const]
            : property_schema_singular.enum
            ? property_schema_singular.enum
            : []; // Shouldn't be a case; TS acting up

        output_enum_types.push({
            name: property_type_name,
            values,
        });

        return {
            value: property_type_name,
            is_array: false,
            is_array_content_nullable: false,

            is_null: false,
            is_nullable: false,
            output_object_types,
            output_union_types,
            output_enum_types,
            output_scalar_types,
        };
    }

    //  F: Date
    else if (
        property_schema_singular.type === SchemaTypeName.string &&
        property_schema_singular.format === SchemaStringFormat.date_time
    ) {
        return {
            value: DEFAULT_ADDITIONAL_PRIMITIVES.Date,
            is_array: false,
            is_array_content_nullable: false,

            is_null: false,
            is_nullable: false,
            output_object_types,
            output_union_types,
            output_enum_types,
            output_scalar_types,
        };
    }

    //  n: Default GraphQL scalar
    else {
        return {
            value: PRIMITIVES[property_schema_singular.type],
            is_array: false,
            is_array_content_nullable: false,

            is_null: false,
            is_nullable: false,
            output_object_types,
            output_union_types,
            output_enum_types,
            output_scalar_types,
        };
    }
}

/**
 * Assert valid property schema
 */
function validatePropertySchema({
    property_schema,
}: Pick<IDeterminePropertyValueInput, 'property_schema'>): void {
    //  allOf not valid
    const property_schema_all_of = (property_schema as unknown) as ISchemaAllOf;
    if (property_schema_all_of.allOf) {
        throw new Error(`Invalid schema: ".allOf" is not supported for GraphQL conversion`);
    }
}

/**
 * Establish a new union type
 */
function createUnionType(
    {
        items,
        property_type_name,
        mode,
        parent_object_name,
        property_name,
        is_required,
    }: { items: Array<ISchemaCoreType>; property_type_name: string } & Omit<
        IDeterminePropertyValueInput,
        'property_schema'
    >,
    {
        output_object_types,
        output_union_types,
        output_enum_types,
        output_scalar_types,
    }: IGraphQLOutputTypes
): IPropertyValue {
    const values = items.map((type, index) =>
        determinePropertyValue(
            {
                mode,
                parent_object_name,
                property_name: `${property_name}_${index}`,
                property_schema: type,
                is_required,
            },
            {
                output_object_types,
                output_union_types,
                output_enum_types,
                output_scalar_types,
            }
        )
    );

    const non_null_types = values.filter(v => !v.is_null);
    const is_nullable = values.some(v => v.is_null);

    //  Validation:
    //  - Union cannot include array type
    //  - Therefore can only proceed with array alone or nullable array
    if (non_null_types.length > 1 && non_null_types.some(v => v.is_array)) {
        throw new Error(`Invalid schema: Array type cannot form part of GraphQL union type`);
    }

    //  - If we _just_ have a null type, invalid
    if (values.every(v => v.is_null)) {
        throw new Error(`Invalid schema: Type for property "${property_name}" cannot just be null`);
    }

    //  - If we have 1 null (A) + 1 non-null (B), we simply have a nullable (non-required) (B)
    else if (is_nullable && non_null_types.length === 1) {
        //  Required? Invalid, since nullable
        if (is_required) {
            throw new Error(`Invalid schema: Cannot require nullable property "${property_name}"`);
        }

        return {
            ...non_null_types[0],
            is_nullable: true,
        };
    }

    //  Push new union type
    output_union_types.push({
        name: property_type_name,
        values: values
            .filter(v => !v.is_null) // Do not include null type in union
            .map(({ value, is_array, is_array_content_nullable }) =>
                is_array && is_array_content_nullable
                    ? `[${value}]`
                    : is_array
                    ? `[${value}!]`
                    : value
            ),
    });

    return {
        value: property_type_name,
        is_array: false,
        is_array_content_nullable: false,
        is_nullable,
        is_null: false,
        output_object_types,
        output_union_types,
        output_enum_types,
        output_scalar_types,
    };
}

/**
 * Stringify: Object type
 */
function stringifyObjectType(mode: IGraphQLObjectMode): (type: IGraphQLObjectType) => string {
    return ({ name, properties }: IGraphQLObjectType): string => {
        return `${mode} ${name} {
    ${properties
        .map(({ property_name, value, is_required, is_array, is_array_content_nullable }) => {
            const s =
                is_array && is_array_content_nullable
                    ? `${property_name}: [${value}]`
                    : is_array
                    ? `${property_name}: [${value}!]`
                    : `${property_name}: ${value}`;

            return is_required ? `${s}!` : s;
        })
        .join('\n    ')}
}`;
    };
}

/**
 * Stringify: Union type
 */
function stringifyUnionType({ name, values }: IGraphQLUnionType): string {
    return `union ${name} = ${Array.from(new Set(values)).join(' | ')}`;
}

/**
 * Stringify: Enum type
 */
function stringifyEnumType({ name, values }: IGraphQLEnumType): string {
    return `enum ${name} {
    ${Array.from(new Set(values)).join('\n    ')}
}`;
}

/**
 * Stringify: Scalar
 */
function stringifyScalar(scalar: IGraphQLScalar): string {
    return `scalar ${scalar}`;
}
